import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Heart, ArrowLeft, Calendar, Share2 } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TribePostPage() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/tribe/posts/${id}`);
        setPost(response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          setNotFound(true);
        }
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.subtitle || "A reflection from Elise Redfield",
          url: window.location.href
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  // Convert plain text to formatted paragraphs
  const renderContent = (content) => {
    // Split by double newlines OR single newlines before headings/quotes
    const blocks = content.split(/\n\n|\n(?=##|###|>|\- )/);
    
    return blocks.map((block, index) => {
      const trimmed = block.trim();
      if (!trimmed) return null;
      
      // Check if it's a heading (starts with ##)
      if (trimmed.startsWith('## ')) {
        return (
          <h2 key={index} className="font-heading text-2xl tracking-wide uppercase text-graphite-dark mt-12 mb-4">
            {trimmed.replace('## ', '')}
          </h2>
        );
      }
      if (trimmed.startsWith('### ')) {
        return (
          <h3 key={index} className="font-heading text-xl tracking-wide uppercase text-graphite-dark mt-8 mb-3">
            {trimmed.replace('### ', '')}
          </h3>
        );
      }
      // Check if it's a quote (starts with >)
      if (trimmed.startsWith('> ')) {
        return (
          <blockquote key={index} className="border-l-4 border-sand pl-6 py-2 my-8 font-accent italic text-xl text-graphite/70">
            {trimmed.replace('> ', '').replace(/^"/, '').replace(/"$/, '')}
          </blockquote>
        );
      }
      // Check if it's a list (contains lines starting with -)
      if (trimmed.includes('\n- ') || trimmed.startsWith('- ')) {
        const items = trimmed.split('\n').filter(line => line.trim().startsWith('- '));
        return (
          <ul key={index} className="my-6 space-y-3">
            {items.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-sand-dark mt-2.5 flex-shrink-0" />
                <span className="font-body text-lg text-graphite/80 leading-relaxed">{item.replace(/^- /, '')}</span>
              </li>
            ))}
          </ul>
        );
      }
      // Regular paragraph
      return (
        <p key={index} className="font-body text-lg text-graphite/80 leading-relaxed mb-6">
          {trimmed}
        </p>
      );
    }).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="animate-pulse text-graphite/50">Loading...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-bone">
        <Navigation />
        <div className="pt-32 px-6 text-center">
          <h1 className="font-heading text-4xl text-graphite-dark mb-4">Post Not Found</h1>
          <p className="font-body text-graphite/60 mb-8">This reflection may have been removed or doesn't exist.</p>
          <Link
            to="/tribe"
            className="inline-flex items-center gap-2 text-sand-dark hover:text-graphite-dark transition-colors"
          >
            <ArrowLeft size={16} />
            Back to My Tribe
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <Navigation />
      
      <article className="pt-28 pb-20">
        {/* Featured Image */}
        {post.featured_image && (
          <div className="w-full h-[50vh] md:h-[60vh] mb-12">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="max-w-screen-md mx-auto px-6">
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-12"
          >
            <Link
              to="/tribe"
              className="inline-flex items-center gap-2 font-body text-sm tracking-wider uppercase text-graphite/50 hover:text-graphite-dark transition-colors"
            >
              <ArrowLeft size={14} />
              Back to My Tribe
            </Link>
          </motion.div>

          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            {/* Date & Share */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-sand-dark" />
                <time className="font-body text-xs tracking-wider uppercase text-graphite/50">
                  {formatDate(post.published_at || post.created_at)}
                </time>
              </div>
              <button
                onClick={handleShare}
                className="p-2 text-graphite/40 hover:text-graphite-dark transition-colors"
                aria-label="Share post"
              >
                <Share2 size={18} />
              </button>
            </div>

            {/* Title */}
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase text-graphite-dark mb-4">
              {post.title}
            </h1>

            {/* Subtitle */}
            {post.subtitle && (
              <p className="font-accent text-xl md:text-2xl italic text-graphite/60">
                {post.subtitle}
              </p>
            )}
          </motion.header>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-12">
            <div className="flex-1 h-px bg-sand/30" />
            <Heart size={16} className="text-sand" fill="#D4C5B5" />
            <div className="flex-1 h-px bg-sand/30" />
          </div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose-elise"
          >
            {renderContent(post.content)}
          </motion.div>

          {/* Signature */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16 pt-12 border-t border-sand/30"
          >
            <p className="font-accent text-xl italic text-graphite/70 mb-2">
              With strength and grace,
            </p>
            <p className="font-heading text-2xl tracking-wide uppercase text-graphite-dark">
              Elise
            </p>
          </motion.div>

          {/* Subscribe CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-16 p-8 bg-[#F5F0EA] text-center"
          >
            <Heart size={24} className="text-sand-dark mx-auto mb-4" fill="#A89A80" />
            <h3 className="font-heading text-xl tracking-wide uppercase text-graphite-dark mb-3">
              Enjoyed this reflection?
            </h3>
            <p className="font-body text-sm text-graphite/60 mb-6">
              Join My Tribe and receive weekly insights directly to your inbox.
            </p>
            <Link
              to="/#my-tribe"
              className="inline-flex items-center gap-2 px-6 py-3 bg-graphite-dark text-bone font-heading tracking-wider uppercase text-sm hover:bg-black transition-colors"
            >
              Subscribe
            </Link>
          </motion.div>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-graphite/5">
        <div className="max-w-screen-lg mx-auto text-center">
          <Link to="/" className="font-heading text-sm tracking-[0.2em] uppercase text-graphite/40 hover:text-graphite-dark transition-colors">
            Elise Redfield
          </Link>
        </div>
      </footer>
    </div>
  );
}
